<template>
  <section class="aggregation-panel">
    <template v-if="event">
      <header class="aggregation-panel__header">
        <div class="aggregation-panel__identity">
          <span class="aggregation-panel__eyebrow">
            This alert belongs to
          </span>
          <h3 class="aggregation-panel__title">
            <router-link :to="`/change-events/detail/${event.id}`">
              {{ event.summary || 'Aggregated alert' }}
            </router-link>
          </h3>
        </div>

        <SeverityMix :severity="event.severity" :mix="eventSeverityMix" />
      </header>

      <div class="aggregation-panel__body">
        <div class="aggregation-panel__metrics">
          <div class="aggregation-panel__metric">
            <span>Alerts grouped</span>
            <strong>{{ formatNumber(event.alert_count) }}</strong>
          </div>
          <div class="aggregation-panel__metric">
            <span>Pages affected</span>
            <strong>{{ formatNumber(event.page_count) }}</strong>
          </div>
        </div>

        <dl class="aggregation-panel__details">
          <div v-if="eventCategory">
            <dt>Category</dt>
            <dd>{{ eventCategory }}</dd>
          </div>
          <div v-if="eventChangeType">
            <dt>Change type</dt>
            <dd>{{ eventChangeType }}</dd>
          </div>
          <div v-if="event.join_reason">
            <dt>Grouped because</dt>
            <dd>{{ event.join_reason }}</dd>
          </div>
        </dl>
      </div>
    </template>

    <div
      v-if="siteIncident"
      class="aggregation-panel__site-incident"
      :class="{ 'aggregation-panel__site-incident--standalone': !event }"
    >
      <span class="aggregation-panel__eyebrow">
        Site-wide incident
      </span>
      <router-link
        class="aggregation-panel__site-link"
        :to="`/change-events/site-incidents/${siteIncident.id}`"
      >
        {{ siteIncident.site || 'This website' }}
      </router-link>
      <span
        v-if="siteIncidentCategory"
        class="aggregation-panel__site-category"
        :class="{
          'aggregation-panel__site-category--danger':
            siteIncidentCategory === 'Potential Site-Wide Defacement'
        }"
      >
        {{ siteIncidentCategory }}
      </span>
    </div>
  </section>
</template>

<script>
import SeverityMix from '@modules/ChangeEvents/components/SeverityMix.vue';
import {
  categoryLabel,
  changeTypeLabel,
  severityMix
} from '@modules/ChangeEvents/consts/ChangeEvents.consts';

export default {
  name: 'AggregatedAlertPanel',
  components: {
    SeverityMix
  },
  props: {
    event: {
      type: Object,
      default: null
    },
    siteIncident: {
      type: Object,
      default: null
    }
  },
  computed: {
    eventSeverityMix() {
      return severityMix(this.event);
    },
    eventCategory() {
      return this.event ? categoryLabel(this.event) : '';
    },
    eventChangeType() {
      return this.event ? changeTypeLabel(this.event) : '';
    },
    siteIncidentCategory() {
      return this.siteIncident ? categoryLabel(this.siteIncident) : '';
    }
  },
  methods: {
    formatNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number.toLocaleString('en-US') : '0';
    }
  }
};
</script>

<style lang="scss" scoped>
.aggregation-panel {
  overflow: hidden;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;

  &__header {
    display: flex;
    gap: 14px;
    align-items: center;
    justify-content: space-between;
    padding: 9px 10px;
  }

  &__eyebrow {
    color: #8a94a0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    line-height: 15px;
    text-transform: uppercase;
  }

  &__identity {
    display: flex;
    gap: 4px;
    align-items: baseline;
  }

  &__title {
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    line-height: 15px;
    text-transform: uppercase;

    a {
      color: #20a4b8;
      text-decoration: none;

      &:hover,
      &:focus {
        color: #187d8e;
        text-decoration: underline;
      }
    }
  }

  &__body {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 18px;
    align-items: stretch;
    padding: 8px 10px 9px;
    background: #fafafa;
    border-top: 1px solid #eeeeee;
  }

  &__metrics {
    display: flex;
  }

  &__metric {
    display: flex;
    min-width: 100px;
    flex-direction: column;

    & + & {
      padding-left: 12px;
      border-left: 1px solid #e5e5e5;
    }

    strong {
      color: rgba(0, 0, 0, 0.85);
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
    }

    span {
      margin-bottom: 3px;
      color: #737d87;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      line-height: 15px;
      text-transform: uppercase;
    }
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(3, minmax(130px, 1fr));
    gap: 8px 18px;
    align-items: start;
    margin: 0;

    div {
      min-width: 0;
    }

    dt {
      margin: 0 0 3px;
      color: #737d87;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      line-height: 15px;
      text-transform: uppercase;
    }

    dd {
      margin: 0;
      color: rgba(0, 0, 0, 0.7);
      font-size: 12.5px;
      line-height: 18px;
    }
  }

  &__site-incident {
    display: flex;
    gap: 8px;
    align-items: baseline;
    padding: 8px 10px;
    border-top: 1px solid #eeeeee;

    &--standalone {
      border-top: 0;
    }
  }

  &__site-link {
    color: #20a4b8;
    font-weight: 600;
    line-height: 18px;
  }

  &__site-category {
    padding-left: 10px;
    color: #8a94a0;
    font-size: 12px;
    line-height: 18px;
    border-left: 1px solid #d9d9d9;

    &--danger {
      color: #ff4d4f;
      font-weight: 600;
    }
  }
}

@media (max-width: 900px) {
  .aggregation-panel {
    &__body {
      grid-template-columns: 1fr;
      gap: 14px;
    }
  }
}

@media (max-width: 600px) {
  .aggregation-panel {
    &__header {
      align-items: flex-start;
      flex-direction: column;
      gap: 10px;
    }

    &__details {
      grid-template-columns: 1fr;
    }

    &__site-incident {
      align-items: flex-start;
      flex-direction: column;
      gap: 3px;
    }

    &__site-category {
      padding-left: 0;
      border-left: 0;
    }
  }
}
</style>
